import type { TranslationKey } from '@/i18n/translation-key';
import type { FlowContributorStatusIconColor } from '@/utils/contributor-extraction.utils';

const OVERFLOW_MENU = 'event.detail.contributors.overflowMenu' as const;

export type ContributorItemLabelContext = 'session' | 'flowRole' | 'flowSlot';

type Translate = (key: TranslationKey) => string;

const menuKey = (suffix: string) => `${OVERFLOW_MENU}.${suffix}` as TranslationKey;

/** Overflow menu action label (toggle remote ↔ in-person). */
export function contributorRemoteSpeakerActionLabel(t: Translate, remoteSpeakerEnabled: boolean): string {
  return remoteSpeakerEnabled ? t(menuKey('makeInPersonSpeaker')) : t(menuKey('makeRemoteSpeaker'));
}

const STATUS_ICON = 'event.detail.contributors.remoteSpeaker' as const;

/**
 * Status-icon tooltip — explains the icon color (separate from overflow menu labels).
 * Pass `slotIsRemote: false` for the in-person / enable-remote icon.
 * Use `context: 'contributorRow'` for the contributor main-row icon (success = quota assigned).
 */
export function contributorRemoteSpeakerStatusIconTooltip(
  t: Translate,
  options: {
    slotIsRemote: boolean;
    color?: FlowContributorStatusIconColor | null;
    context?: 'appearance' | 'contributorRow';
  },
): string {
  if (!options.slotIsRemote) {
    return t(`${STATUS_ICON}.statusIconInPersonTooltip` as TranslationKey);
  }
  if (options.color === 'primary' || options.color === 'warning') {
    return t(`${STATUS_ICON}.statusIconWarningTooltip` as TranslationKey);
  }
  if (options.color === 'success') {
    return options.context === 'contributorRow'
      ? t(`${STATUS_ICON}.statusIconSuccessQuotaTooltip` as TranslationKey)
      : t(`${STATUS_ICON}.statusIconSuccessTooltip` as TranslationKey);
  }
  return t(`${STATUS_ICON}.statusIconInfoTooltip` as TranslationKey);
}

export function contributorVisibilityActionLabel(
  t: Translate,
  labelContext: ContributorItemLabelContext,
  visibilityEnabled: boolean,
): string {
  if (visibilityEnabled) {
    if (labelContext === 'flowSlot') return t(menuKey('hideOnThisSlot'));
    if (labelContext === 'flowRole') return t(menuKey('hideOnThisFlow'));
    return t(menuKey('hideOnThisSession'));
  }
  if (labelContext === 'flowSlot') return t(menuKey('showOnThisSlot'));
  if (labelContext === 'flowRole') return t(menuKey('showOnThisFlow'));
  return t(menuKey('showOnThisSession'));
}
